/**
 * Goods Allocation — Mass Upload dialog (Access frmProcurementQryGoodAlc_AddMany /
 * frmProcurementQryGoodAlcMany, RecordSource public_tmp_addmany_goodsalc).
 *
 * Style theo Futures Orders › Mass upload: grid Excel-like — dán TRỰC TIẾP vào ô
 * (onPasteCapture, tự nở dòng theo vùng dán) + checkbox chọn dòng + Copy/Delete/Reset.
 * Cột lookup (Contract/Grade/Inspection/Warehouse) = Autocomplete. Save → addMany.
 */
import { useMemo, useState, type ClipboardEvent } from 'react'

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Popper,
  type PopperProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useAddManyGoodAllocationsMutation,
  useGoodAllocConsignmentLookupQuery,
  useGoodAllocGradeLookupQuery,
  useGoodAllocInspectionLookupQuery,
  useGoodAllocPContractLookupQuery,
  useGoodAllocWarehouseLookupQuery,
} from '../../store/api/procurementGoodAllocationApi'
import type { GoodAllocationLookupItem, GoodAllocationWrite } from '../../types/api'
import { getErrorMessage } from '../../utils/error'
import {
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
} from '../purchase/dialogStyle'

// Compact look cho grid mass-upload (đồng bộ Futures Orders › Mass upload).
const massCellSx = { px: 0.5, py: 0.25 }
// Height CỐ ĐỊNH cho MỌI field (TextField / date / Autocomplete) → cùng chiều cao.
const MASS_FIELD_H = 32
const massInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    height: MASS_FIELD_H,
    minHeight: MASS_FIELD_H,
    fontSize: '0.72rem',
    py: 0,
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': { py: 0, fontSize: '0.72rem' },
  // Autocomplete input có padding riêng → ép về 0 để cao bằng TextField.
  '& .MuiAutocomplete-input': { py: '0 !important', fontSize: '0.72rem' },
}
const massHeaderSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
  bgcolor: '#fafafa',
}
const toolbarBtnSx: SxProps<Theme> = {
  fontSize: '0.7rem',
  py: 0.25,
  px: 0.875,
  minHeight: 26,
  textTransform: 'none',
}

/** Cột nhập liệu theo đúng thứ tự dán TSV từ Excel. */
type ColKind = 'text' | 'date' | 'number'
/** Cột lookup (Access ComboBox trong form New/Edit) → render Autocomplete. */
type ComboKind = 'contract' | 'grade' | 'inspection' | 'warehouse'
interface ColDef {
  key: keyof GoodAllocationWrite
  label: string
  kind: ColKind
  width: number
  combo?: ComboKind
}

// Thứ tự + caption bám 1:1 bảng Access `tmp_addmany_goodsalc` (11 cột; `ID save`
// là PK nội bộ, không dán). truck_status/truck_id/receipt_no KHÔNG thu ở mass
// upload — VBA hardcode truck_status='Estimated' khi INSERT (xem toWrite).
const COLUMNS: ColDef[] = [
  { key: 'contract_no', label: 'Contract No', kind: 'text', width: 180, combo: 'contract' },
  { key: 'm_date', label: 'M Date', kind: 'date', width: 128 },
  { key: 'm_truck_no', label: 'M Truck No', kind: 'text', width: 120 },
  { key: 'estimated_quantity', label: 'Estimated Quantity', kind: 'number', width: 130 },
  { key: 'quality_code', label: 'Quality Code', kind: 'text', width: 120, combo: 'grade' },
  { key: 'inspection', label: 'Inspection Name', kind: 'text', width: 160, combo: 'inspection' },
  { key: 'phone_no', label: 'Phone No', kind: 'text', width: 120 },
  { key: 'to_warehouse', label: 'To Warehouse', kind: 'text', width: 150, combo: 'warehouse' },
  { key: 'allocating_quantity', label: 'Allocated Kg', kind: 'number', width: 110 },
  { key: 'allocating_bag', label: 'Allocated Bag', kind: 'number', width: 110 },
  { key: 'note', label: 'Note', kind: 'text', width: 200 },
]

/** Dòng nội bộ dạng string để dễ edit; convert sang GoodAllocationWrite khi Save. */
type RowDraft = Record<keyof GoodAllocationWrite, string>

let ROW_SEQ = 0
function emptyRow(): { id: number; data: RowDraft } {
  const data = {} as RowDraft
  COLUMNS.forEach((c) => {
    data[c.key] = ''
  })
  data.m_date = todayIso()
  return { id: ++ROW_SEQ, data }
}

/** Ngày hiện tại dạng YYYY-MM-DD — mặc định cho M Date dòng mới. */
function todayIso(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

/** Parse TSV từ clipboard → ma trận string[][] (dán tại ô → nở theo vùng). */
function parseMatrix(text: string): string[][] {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n$/, '')
    .split('\n')
    .map((line) => line.split('\t'))
}

function buildIsoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (year < 1900 || year > 2999) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Normalize ô ngày dán từ Excel → YYYY-MM-DD (ambiguous → D-M-Y kiểu VN). */
function parsePastedDate(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    return buildIsoDate(+iso[1], +iso[2], +iso[3]) ?? buildIsoDate(+iso[1], +iso[3], +iso[2]) ?? ''
  }
  const sep = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/)
  if (sep) {
    const p1 = +sep[1]
    const p2 = +sep[2]
    const year = sep[3].length === 2 ? 2000 + +sep[3] : +sep[3]
    let day: number, month: number
    if (p1 > 12 && p2 <= 12) [day, month] = [p1, p2]
    else if (p2 > 12 && p1 <= 12) [day, month] = [p2, p1]
    else [day, month] = [p1, p2]
    return buildIsoDate(year, month, day) ?? ''
  }
  const t = new Date(s)
  if (!Number.isNaN(t.getTime())) return buildIsoDate(t.getFullYear(), t.getMonth() + 1, t.getDate()) ?? ''
  return ''
}

/** Chỉ feed YYYY-MM-DD hợp lệ cho <input type="date"> (tránh warning React). */
function safeDateValue(v: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : ''
}

/** Numeric format = form New (Access Format Standard: thousand sep + 2dp). */
function parseNumeric(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function formatStandard(v: string): string {
  const n = parseNumeric(v)
  return n == null ? '' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function toWrite(data: RowDraft): GoodAllocationWrite {
  const out: GoodAllocationWrite = {}
  COLUMNS.forEach((c) => {
    const v = (data[c.key] ?? '').trim()
    if (v === '') {
      out[c.key] = null as never
      return
    }
    if (c.kind === 'number') {
      const n = Number(v.replace(/,/g, ''))
      out[c.key] = (Number.isFinite(n) ? n : null) as never
    } else {
      out[c.key] = v as never
    }
  })
  // VBA AddMany INSERT hardcode truck_status='Estimated' (không thu ở datasheet).
  out.truck_status = 'Estimated' as never
  return out
}

/** Dòng có nhập ít nhất 1 ô? (bỏ dòng hoàn toàn trống). */
function isRowFilled(data: RowDraft): boolean {
  return COLUMNS.some((c) => (data[c.key] ?? '').trim() !== '')
}

/** Popper cho ComboCell: dropdown rộng theo nội dung (value dài đọc đủ). */
function WideComboPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: 'fit-content', minWidth: 260, maxWidth: 560 }}
    />
  )
}

/** Cell combobox lookup — pattern /convert-form: Autocomplete strict, inject value
 *  đã dán vào options. Field CHỈ hiện keyword (code); dropdown hiện "<CODE> — <NAME>". */
function ComboCell({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string
  options: GoodAllocationLookupItem[]
  onChange: (v: string) => void
  disabled: boolean
}) {
  const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : undefined)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size="small"
      fullWidth
      options={merged}
      value={sel}
      disabled={disabled}
      disableClearable
      onChange={(_e, v) => onChange(v?.value ?? '')}
      PopperComponent={WideComboPopper}
      getOptionLabel={(o) => (o ? String(o.value ?? '') : '')}
      renderOption={(props, o, state) => {
        const code = String(o.value ?? '')
        const lab = String(o.label ?? '')
        const text = !lab || lab === code ? code : `${code} — ${lab}`
        const { key, ...rest } = props as typeof props & { key?: React.Key }
        return (
          <li {...rest} key={`${code}__${lab}__${state.index}`} style={{ whiteSpace: 'nowrap' }}>
            {text}
          </li>
        )
      }}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderInput={(p) => <TextField {...p} sx={massInputSx} />}
    />
  )
}

export interface GoodAllocationMassUploadDialogProps {
  open: boolean
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function GoodAllocationMassUploadDialog({
  open,
  canEdit,
  onClose,
  onSaved,
}: GoodAllocationMassUploadDialogProps) {
  const snackbar = useSnackbar()
  const [rows, setRows] = useState<{ id: number; data: RowDraft }[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  // Copy/Paste trong app — KHÔNG dùng browser Clipboard API (origin http nội bộ không hỗ trợ,
  // xem feedback "unable to copy"). Copy dòng đã chọn vào state, Paste ghi đè dòng chọn / thêm cuối bảng.
  const [copiedRows, setCopiedRows] = useState<RowDraft[]>([])
  const [addMany, { isLoading }] = useAddManyGoodAllocationsMutation()

  // Lookups cho các cột combobox (Access ComboBox form New/Edit).
  const { data: pContractData } = useGoodAllocPContractLookupQuery()
  const { data: consignmentData } = useGoodAllocConsignmentLookupQuery()
  const { data: gradeData } = useGoodAllocGradeLookupQuery()
  const { data: inspectionData } = useGoodAllocInspectionLookupQuery()
  const { data: warehouseData } = useGoodAllocWarehouseLookupQuery()

  const comboOptions = useMemo<Record<ComboKind, GoodAllocationLookupItem[]>>(
    () => ({
      // Contract No mass-upload = 1 cột → gộp P + Consignment (Access chọn 1 trong 2).
      contract: [...(pContractData?.items ?? []), ...(consignmentData?.items ?? [])],
      grade: gradeData?.items ?? [],
      inspection: inspectionData?.items ?? [],
      warehouse: warehouseData?.items ?? [],
    }),
    [pContractData, consignmentData, gradeData, inspectionData, warehouseData],
  )

  const filledCount = useMemo(() => rows.filter((r) => isRowFilled(r.data)).length, [rows])
  const allSelected = rows.length > 0 && selected.size === rows.length

  /** Chuẩn hoá 1 ô dán theo loại cột (combo → phải khớp value trong list, không khớp thì
   *  bỏ trống; date → ISO, sai format thì bỏ trống; số → bỏ phẩy, không phải số thì bỏ trống). */
  const coerceCell = (col: ColDef, raw: string): string => {
    const v = (raw ?? '').trim()
    if (!v) return ''
    if (col.combo) {
      const hit = comboOptions[col.combo].find((o) => String(o.value).toLowerCase() === v.toLowerCase())
      return hit ? String(hit.value) : ''
    }
    if (col.kind === 'date') return parsePastedDate(v)
    if (col.kind === 'number') return formatStandard(v)
    return v
  }

  const setCell = (index: number, key: keyof GoodAllocationWrite, value: string) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, data: { ...r.data, [key]: value } } : r)))

  /** Format Standard (thousand sep + 2dp) khi blur ô số — giống form New. */
  const handleNumericBlur = (index: number, key: keyof GoodAllocationWrite) => () =>
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, data: { ...r.data, [key]: formatStandard(r.data[key]) } } : r)),
    )

  /** Áp ma trận dán vào rows tại (startRow,startCol) — nở dòng theo vùng dán. */
  const applyMatrix = (
    prev: { id: number; data: RowDraft }[],
    startRow: number,
    startCol: number,
    matrix: string[][],
  ) => {
    const next = prev.map((r) => ({ id: r.id, data: { ...r.data } }))
    while (next.length < startRow + matrix.length) next.push(emptyRow())
    matrix.forEach((cells, r) => {
      const target = next[startRow + r]
      cells.forEach((raw, c) => {
        const fi = startCol + c
        if (fi >= COLUMNS.length) return
        const col = COLUMNS[fi]
        target.data[col.key] = coerceCell(col, raw)
      })
    })
    return next
  }

  /** Dán tại ô (rowIdx,colIdx) — nở dòng theo vùng dán, điền theo thứ tự cột. */
  const handlePaste = (rowIdx: number, colIdx: number, e: ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text) return
    // Ô đơn (không tab/newline) → để input tự xử lý paste bình thường.
    if (!text.includes('\t') && !text.includes('\n')) return
    e.preventDefault()
    e.stopPropagation()
    const matrix = parseMatrix(text)
    if (!matrix.length) return
    setRows((prev) => applyMatrix(prev, rowIdx, colIdx, matrix))
  }

  /** Nút Paste — LUÔN thêm copiedRows (giá trị Copy gần nhất) vào CUỐI bảng, không phụ
   *  thuộc dòng đang chọn. Bấm lặp lại nhiều lần → mỗi lần thêm 1 lượt copiedRows mới,
   *  luôn đúng giá trị vừa Copy gần nhất (không ghi đè dòng cũ, tránh nhầm dòng đích). */
  const pasteRows = () => {
    if (!copiedRows.length) {
      snackbar.error('Copy a row first.')
      return
    }
    setRows((prev) => [...prev, ...copiedRows.map((data) => ({ id: ++ROW_SEQ, data: { ...data } }))])
    snackbar.success(`Pasted ${copiedRows.length} row${copiedRows.length === 1 ? '' : 's'}`)
  }

  const addRow = () => setRows((prev) => [...prev, emptyRow()])

  const toggleSel = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })

  const toggleSelAll = () =>
    setSelected((prev) => (prev.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    setRows((prev) => {
      const next = prev.filter((_, i) => !selected.has(i))
      return next.length ? next : [emptyRow()]
    })
    setSelected(new Set())
  }

  /** Copy bằng Selection API trên 1 div contenteditable ẩn (KHÔNG dùng textarea.focus()) —
   *  giống Futures Orders mass-upload. textarea.focus() bị MUI Dialog FocusTrap giữ focus
   *  trong dialog nên execCommand trả true nhưng clipboard rỗng/sai; Selection API không
   *  cần focus nên chạy đúng trong Dialog, kể cả trên origin http (navigator.clipboard undefined). */
  const copyViaSelection = (html: string): boolean => {
    const div = document.createElement('div')
    div.contentEditable = 'true'
    div.innerHTML = html
    div.style.position = 'fixed'
    div.style.top = '-9999px'
    div.style.left = '-9999px'
    div.style.opacity = '0'
    document.body.appendChild(div)
    const selection = window.getSelection()
    if (!selection) {
      document.body.removeChild(div)
      return false
    }
    const savedRanges: Range[] = []
    for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
    const range = document.createRange()
    range.selectNodeContents(div)
    selection.removeAllRanges()
    selection.addRange(range)
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    selection.removeAllRanges()
    savedRanges.forEach((r) => selection.addRange(r))
    document.body.removeChild(div)
    return ok
  }

  const copyViaTextarea = (text: string): boolean => {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    document.body.appendChild(ta)
    const prevActive = document.activeElement as HTMLElement | null
    ta.focus()
    ta.select()
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    document.body.removeChild(ta)
    prevActive?.focus?.()
    return ok
  }

  /** Nút Copy — lưu dòng đã chọn (hoặc toàn bộ nếu không chọn) vào state trong app (để
   *  dùng nút Paste nhân dòng ngay trên web) ĐỒNG THỜI ghi vào clipboard hệ thống dạng
   *  TSV+HTML (để Ctrl+V dán được sang Excel, giữ đúng cột). */
  const copyRows = async () => {
    const idx = selected.size > 0 ? [...selected].sort((a, b) => a - b) : rows.map((_, i) => i)
    setCopiedRows(idx.map((i) => ({ ...rows[i].data })))
    const cellText = (i: number, c: ColDef) => String(rows[i].data[c.key] ?? '').replace(/[\t\r\n]/g, ' ')
    const headerRow = COLUMNS.map((c) => c.label).join('\t')
    const tsv = [headerRow, ...idx.map((i) => COLUMNS.map((c) => cellText(i, c)).join('\t'))].join('\n')
    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const html =
      '<table>' +
      '<tr>' +
      COLUMNS.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('') +
      '</tr>' +
      idx
        .map((i) => '<tr>' + COLUMNS.map((c) => `<td>${escapeHtml(cellText(i, c))}</td>`).join('') + '</tr>')
        .join('') +
      '</table>'

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })
        await navigator.clipboard.write([item])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection(html)
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea(tsv)

    if (ok) snackbar.success(`Copied ${idx.length} row${idx.length === 1 ? '' : 's'} — paste into the grid or Excel.`)
    else snackbar.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
  }

  const resetForm = () => {
    setRows([emptyRow()])
    setSelected(new Set())
  }

  const handleClose = () => {
    if (isLoading) return
    resetForm()
    onClose()
  }

  const handleSave = async () => {
    const payload = rows.filter((r) => isRowFilled(r.data)).map((r) => toWrite(r.data))
    if (!payload.length) {
      snackbar.warning('Please enter at least one row.')
      return
    }
    try {
      const res = await addMany(payload).unwrap()
      snackbar.success(res.message ?? `Successfully Added ${payload.length} records`)
      resetForm()
      onSaved()
      onClose()
    } catch (err) {
      snackbar.error(getErrorMessage(err))
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Inbound Logistics - Goods Allocation - Mass Upload"
          sx={dialogTitleTextSx}
        >
          Inbound Logistics - Goods Allocation - Mass Upload
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canEdit || filledCount === 0 || isLoading}
            sx={dialogHeaderBtnSx}
          >
            {isLoading ? <CircularProgress size={14} color="inherit" /> : `Save${filledCount ? ` (${filledCount})` : ''}`}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={isLoading}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />

      <DialogContent dividers>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Ctrl+V a cell to paste from Excel, or select row(s) → Copy to also copy to Excel — the Paste button re-adds the last copied row(s) here.
            </Typography>
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={addRow}
                disabled={!canEdit}
                sx={toolbarBtnSx}
              >
                Add row
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => void copyRows()}
                sx={toolbarBtnSx}
              >
                {selected.size > 0 ? `Copy (${selected.size})` : 'Copy all'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentPasteIcon sx={{ fontSize: 14 }} />}
                onClick={pasteRows}
                disabled={!canEdit || !copiedRows.length}
                sx={toolbarBtnSx}
              >
                Paste as new row(s)
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                disabled={!canEdit || selected.size === 0}
                onClick={deleteSelected}
                sx={toolbarBtnSx}
              >
                {selected.size > 0 ? `Delete (${selected.size})` : 'Delete selected'}
              </Button>
              <Button size="small" variant="text" onClick={resetForm} disabled={!canEdit} sx={toolbarBtnSx}>
                Reset
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ maxHeight: '58vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
            <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" align="center" sx={{ ...massHeaderSx, width: 56, textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>
                    <Checkbox
                      size="small"
                      indeterminate={selected.size > 0 && !allSelected}
                      checked={allSelected}
                      onChange={toggleSelAll}
                      disabled={!canEdit}
                      sx={{ p: 0.25 }}
                    />
                  </TableCell>
                  {COLUMNS.map((c, i) => (
                    <TableCell key={c.key} sx={{ ...massHeaderSx, width: c.width, ...(i === 0 ? { pl: 1.5 } : {}) }}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => {
                  const isSel = selected.has(index)
                  return (
                    <TableRow key={r.id} hover selected={isSel}>
                      <TableCell padding="checkbox" align="center" sx={{ width: 56, textAlign: 'center', borderRight: '1px solid #eeeeee' }}>
                        <Checkbox
                          size="small"
                          checked={isSel}
                          onChange={() => toggleSel(index)}
                          disabled={!canEdit}
                          sx={{ p: 0.25 }}
                        />
                      </TableCell>
                      {COLUMNS.map((c, colIdx) => (
                        <TableCell
                          key={c.key}
                          sx={{ ...massCellSx, ...(colIdx === 0 ? { pl: 1.5 } : {}) }}
                          onPasteCapture={(e) => handlePaste(index, colIdx, e)}
                        >
                          {c.combo ? (
                            <ComboCell
                              value={r.data[c.key]}
                              options={comboOptions[c.combo]}
                              onChange={(v) => setCell(index, c.key, v)}
                              disabled={!canEdit}
                            />
                          ) : (
                            <TextField
                              value={c.kind === 'date' ? safeDateValue(r.data[c.key]) : r.data[c.key]}
                              onChange={(e) => setCell(index, c.key, e.target.value)}
                              onBlur={c.kind === 'number' ? handleNumericBlur(index, c.key) : undefined}
                              disabled={!canEdit}
                              size="small"
                              fullWidth
                              type={c.kind === 'date' ? 'date' : 'text'}
                              InputLabelProps={c.kind === 'date' ? { shrink: true } : undefined}
                              inputProps={
                                c.kind === 'number'
                                  ? { style: { textAlign: 'right' }, inputMode: 'decimal' }
                                  : undefined
                              }
                              sx={massInputSx}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
